import _ from 'lodash'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ProtectionContext } from 'features/globalSetup/contexts/ProtectionContext'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { EditSchema } from '../../schemas/editSchema'
import { Data, filteringRuleFromDTO, UPDATE_FILTERING_RULE } from './api'
import { Variables } from './api/types'
import { UseUpdateFilteringRule } from './types'

export const useUpdateFilteringRule = (): UseUpdateFilteringRule => {
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'prebid.protection.form.actions.update',
  })
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(ProtectionContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_FILTERING_RULE,
    onCompleted: async ({ updateFilteringRule }: Data): Promise<void> => {
      if (updateFilteringRule) {
        const { errors, filteringRule } = updateFilteringRule

        if (errors && errors.length > 0) {
          setErrors(mapGraphQLErrors(errors))
          enqueueSnackbar(t('error'), { variant: 'error' })
        } else if (filteringRule) {
          setErrors({})
          enqueueSnackbar(
            t('success', {
              filteringRuleName: filteringRuleFromDTO(filteringRule).name,
            }),
            { variant: 'success' },
          )
        }
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const update = (data: EditSchema): void => {
    mutateAsync({
      input: {
        ...(data.applicableDomains === 'all' ? _.omit(data, ['domainIds']) : data),
      },
    })
  }

  return { errors, update }
}
